import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import colors from '../../../../themes/main-colors';
import BulbIcon from '../../../../static/bulbIcon.svg';
import IconBolt from '../../../../static/icon-bolt.svg';
import { LocationInformation } from '../location-information-comp/LocationInformation';
import { CustomButtons } from '../common/footer-buttons/ButtonsComp';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import { useReportOutageApi } from '../../hooks/useReportOutageApi';
import { useReportOutageUtils } from '../../utils/utils';
import {
  AlertType,
  OutageLookUpType,
  OutageRestoredAlert,
  ReportOutageBySpListGuestRequest,
  ReportOutageResponse,
} from '../../../../__generated__/pge-types';
import { OutageAcknowledgementCard } from '../outage-acknowledgement-card/OutageAcknowledgementCard';
import GreenCheckIcon from '../../../../static/greenCheckIcon.svg';
import { AccountWithOutageInfo } from '../../types';
import { ToggleSwitch } from '../common/toggle-switch';
import { useIsMobile } from '../../../../util/style-utils';
import { ReportOutageOtherLocationDetails } from '../report-outage-other-location-details/ReportOutageOtherLocationDetails';
import useAuth from '../../../../hooks/useAuth';
import { useOutageLookUp } from '../../hooks/useOutageLookUp';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import { useAccountDetailsList } from '../../hooks/useAccountDetailsList';
import { useOutageAccountListContext } from '../../providers/OutageAccountListProvider';
import PhoneTextField from '../../../phone-text-field';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import { displayPhoneNumberFormat } from '../../../../util/format';
import { OutageTabs } from '../mobile-tabs-component';
import { useErrorNotification } from '../common/error-notification';

const useStyles = makeStyles((theme: Theme) => ({
  box: {
    background: colors.white,
    margin: '0 1.5rem',
    paddingBottom: '1px',
    [theme.breakpoints.down('sm')]: {
      margin: '1.5rem 1.5rem 0 1.5rem',
    },
  },
  whiteCard: {
    padding: '1rem',
  },
  header: {
    fontSize: 20,
    fontWeight: 600,
  },
  subHeader: {
    fontSize: 16,
    marginTop: '1rem',
  },
  yellowCard: {
    background: colors.lightYellow,
    margin: '5px',
    padding: '1rem',
  },
  yellowCardText: {
    fontSize: 18,
  },
  bulbContent: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '1rem',
    gap: '10px',
  },
  contactBox: {
    background: colors.white,
    alignItems: 'center',
    padding: '1rem',
  },
  contactBoxDetails: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'row',
  },
  phoneNumberStyles: {
    fontSize: 16,
    fontWeight: 600,
  },
  nameStyles: {
    fontSize: 16,
    marginLeft: '2rem',
  },
  customPhoneNameStyles: {
    fontSize: 16,
    marginLeft: '3rem',
  },
  contactBoxText: {
    margin: '1rem 0',
    gap: '5px',
    display: 'grid',
  },
  bulbIcon: {
    [theme.breakpoints.down('sm')]: {
      width: '25%',
    },
  },
  boltIcon: {
    marginRight: '10px',
  },
  switchStyles: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'end',
    gap: '5px',
  },
  switchDiv: {
    marginTop: '-4rem',
    position: 'absolute',
    marginLeft: '28rem',
  },
  root: {
    width: 44,
    height: 24,
    padding: 0,
    margin: theme.spacing(1),
  },
  thumb: {
    width: 20,
    height: 20,
  },
  font16: {
    fontSize: 16,
  },
  track1: {
    borderRadius: 32 / 2,
    backgroundColor: colors.sparkBlue,
    opacity: 1,
    transition: theme.transitions.create(['background-color', 'border']),
  },
  switchBase1: {
    padding: 2,
    left: 0,
    '&$checked': {
      transform: 'translateX(20px)',
      color: colors.white,
      '& + $track': {
        backgroundColor: theme.palette.grey[600],
        opacity: 1,
        border: 'none',
      },
    },
    '&$focusVisible $thumb': {
      color: theme.palette.grey[600],
    },
  },
}));

export const OutageReportSummary = () => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const [isReportOutageSuccess, setIsReportOutageSuccess] = useState(false);
  const [contactOptions, setContactOptions] = useState<
    Array<{ value?: string; isSelected?: boolean; id: string }>
  >([]);
  const [callbackPhone, setCallbackPhone] = useState<string>('');
  const [isCallbackPhoneSelected, setIsCallbackPhoneSelected] = useState<
    boolean
  >(false);
  const {
    selectedAccountForOutage,
    setSelectedAccountForOutage,
    multiPremiseInfo,
    setOutageMarkerView,
    setReportOutageOpen,
    singlePremiseInfo,
    outageMarkerView,
  } = useOutageLeftPanelState();
  const accountNumber = selectedAccountForOutage?.accountNumber;
  const premiseInfo = selectedAccountForOutage?.premiseInfos?.[0];
  const customerName = selectedAccountForOutage?.mainCustomerName;
  const {
    reportOutageForLoggedInUser,
    reportOutageForGuest,
  } = useReportOutageApi();
  const { getRequestSource } = useReportOutageUtils();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  const [toggleSwitch, setToggleSwitch] = useState<boolean>(false);
  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const commPreference = selectedAccountForOutage?.commPreferenceInfo;
  const [tabNumber, setTabNumber] = useState<number>(0);
  const { wrapWithLoader } = useWrapWithLoader();
  const { setPage } = useOutageResponsivePageTracker();
  const { setErrorNotification } = useErrorNotification();

  const getContactDetails = () => {
    return commPreference?.[0]?.deliveryTypeDetails?.find(
      details => details?.deliveryType === 'SMS',
    )?.contactDetails;
  };

  useEffect(() => {
    const details = getContactDetails();
    if (details && Array.isArray(details)) {
      const mapped = details.map((d, idx) => ({
        id: d?.value ? `${d.value}-${idx}` : `contact-${idx}`,
        value: typeof d?.value === 'string' ? d.value : undefined,
        isSelected: !!d?.isSelected,
      }));
      setContactOptions(mapped);
    } else {
      setContactOptions([]);
    }
  }, [commPreference, selectedAccountForOutage]);

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const checked = event.target.checked;
    setContactOptions(prev =>
      prev.map(opt =>
        opt.id === name || opt.value === name
          ? { ...opt, isSelected: checked }
          : opt,
      ),
    );
  };

  const handleCallbackPhoneCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsCallbackPhoneSelected(event.target.checked);
  };

  const getEncryptedId = (phoneNumber: string) => {
    return selectedAccountForOutage?.commPreferenceInfo?.[0].deliveryTypeDetails?.[1]?.contactDetails?.filter(
      con => con?.value === phoneNumber,
    )?.[0]?.encryptedContactId;
  };

  const getAlertList = () => {
    return (contactOptions?.map(cont => {
      return {
        contactValue: cont.value,
        encryptedContactId: getEncryptedId(cont.value || ''),
        isSelected: cont.isSelected,
        type: AlertType.Sms,
      };
    }) as unknown) as OutageRestoredAlert[];
  };

  const sendReportOutage = wrapWithLoader(async () => {
    try {
      const searchedPremiseInfo = multiPremiseInfo?.selectedPremiseDetails;
      let request: ReportOutageBySpListGuestRequest;
      if (multiPremiseInfo) {
        request = {
          callbackPhone: '',
          encryptedAccountNumber: searchedPremiseInfo?.encryptedAccountNumber as string,
          encryptedServicePointList: searchedPremiseInfo?.outages?.map(
            outage => outage.encryptedServicePointId,
          ) as string[],
          source: getRequestSource(),
        };
      } else {
        request = {
          callbackPhone: '',
          encryptedAccountNumber: singlePremiseInfo?.encryptedAccountNumber as string,
          encryptedServicePointList:
            singlePremiseInfo?.encryptedServicePointList || [],
          source: getRequestSource(),
        };
      }

      if (
        !isAuthenticated ||
        (isAuthenticated && outageMarkerView === OutageMarkerView.NoOutage)
      ) {
        const response = ((await reportOutageForGuest(request)) as unknown) as {
          reportOutageBySpListGuest: ReportOutageResponse;
        };
        setIsReportOutageSuccess(
          !!response?.reportOutageBySpListGuest?.success,
        );
        setOutageMarkerView(OutageMarkerView.SearchedLocation);
      } else {
        const encryptedPremiseId = premiseInfo?.encryptedPremiseId ?? '';
        const encryptedPersonId =
          selectedAccountForOutage?.encryptedPersonId ?? '';
        const encryptedServicePointId =
          selectedAccountForOutage?.premiseInfos?.[0]?.servicePointDetails?.[0]
            ?.encryptedServicePointId ?? '';
        const result = ((await reportOutageForLoggedInUser({
          accountNumber: accountNumber ?? '',
          encryptedPersonId,
          encryptedPremiseId,
          encryptedServicePointList: [encryptedServicePointId],
          callbackPhone:
            contactOptions
              ?.filter(option => option?.isSelected)
              ?.map(data => data.value)[0] ||
            (isCallbackPhoneSelected
              ? displayPhoneNumberFormat(callbackPhone)
              : undefined),
          source: getRequestSource(),
          outageRestoredAlertList: getAlertList(),
        })) as unknown) as { reportOutageBySpList: ReportOutageResponse };
        setIsReportOutageSuccess(!!result?.reportOutageBySpList?.success);
      }
    } catch {
      setErrorNotification();
    }
  });

  const {
    refetchAccountList,
    accountListWithOutageInfo,
  } = useAccountDetailsList();
  const { setAccountListWithOutageInfo } = useOutageAccountListContext();

  useEffect(() => {
    if (accountListWithOutageInfo?.length) {
      setAccountListWithOutageInfo(accountListWithOutageInfo);
    }
  }, [accountListWithOutageInfo]);

  const buttonOneOnSubmit = wrapWithLoader(async () => {
    if (isAuthenticated) {
      const res = await refetchAccountList();
      setReportOutageOpen(false);
      const accountDetails = res?.data?.getAccountDetails?.find(
        acc => selectedAccountForOutage?.accountNumber === acc.accountNumber,
      );
      const nn: AccountWithOutageInfo = {
        commPreferenceInfo: accountDetails?.commPreferences
          ? accountDetails.commPreferences.filter(
              (cp): cp is any => cp !== null,
            )
          : undefined,
        isPrimaryAccount: false,
        hasKnownOutage:
          accountDetails?.premiseInfo?.[0]?.servicePointDetails?.[0]?.outageInfo
            ?.hasKnownOutage ?? false,
        accountNumber: accountDetails?.accountNumber ?? '',
        addressLocation: [
          accountDetails?.premiseInfo?.[0]?.addressLine1 ?? '',
          accountDetails?.premiseInfo?.[0]?.city ?? '',
          accountDetails?.premiseInfo?.[0]?.state ?? '',
          accountDetails?.premiseInfo?.[0]?.postal ?? '',
        ]
          .filter(p => p)
          .join(', '),
        description: accountDetails?.description ?? '',
        encryptedPersonId: accountDetails?.encryptedPersonId ?? '',
        mainCustomerName: accountDetails?.mainCustomerName ?? '',
        premiseInfos: accountDetails?.premiseInfo
          ? accountDetails.premiseInfo.filter(
              (premise): premise is any => premise !== null,
            )
          : undefined,
      };
      setSelectedAccountForOutage(nn);
    } else {
      const response = await getOutageLookUpQueryResponse({
        encryptedValue:
          multiPremiseInfo?.selectedPremiseEncryptedValue ||
          singlePremiseInfo?.selectedPremiseEncryptedValue,

        lookupType: OutageLookUpType.Premise,
      });
      if (response?.outages?.[0].hasKnownOutage) {
        setOutageMarkerView(OutageMarkerView.SearchedLocation);
      } else {
        setOutageMarkerView(OutageMarkerView.NoOutage);
      }
      setPage(ResponsivePageView.Details);
      setReportOutageOpen(false);
    }
  });

  const renderSwitchComp = () => {
    return (
      <div className={classes.switchStyles}>
        <Typography className={classes.font16}>{t('MY_ACCOUNT')}</Typography>
        <ToggleSwitch
          classes={{
            root: classes.root,
            thumb: classes.thumb,
            track: classes.track1,
            switchBase: classes.switchBase1,
          }}
          size={'medium'}
          checked={toggleSwitch}
          onChange={e => {
            setToggleSwitch(e.target.checked);
          }}
        />
        <Typography className={classes.font16}>
          {t('ANOTHER_LOCATION')}
        </Typography>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <>
        <Box className={classes.box}>
          <div className={classes.whiteCard}>
            {isAuthenticated && outageMarkerView !== OutageMarkerView.NoOutage && (
              <>
                <Typography className={classes.header}>
                  {t('YOUR_OUTAGE_REPORT_SUMMARY')}
                </Typography>
                <Typography className={classes.subHeader}>
                  {t('SAME_LOCATION_CONTACT_NUMBER')}
                </Typography>
              </>
            )}
            <LocationInformation
              accountNumber={accountNumber || ''}
              nickName={selectedAccountForOutage?.description || ''}
              addressLine1={premiseInfo?.addressLine1 || ''}
              city={premiseInfo?.city || ''}
              state={premiseInfo?.state || ''}
              postal={premiseInfo?.postal || ''}
            />
          </div>
          {isAuthenticated && outageMarkerView !== OutageMarkerView.NoOutage && (
            <div className={classes.yellowCard}>
              <div className={classes.bulbContent}>
                <BulbIcon className={classes.bulbIcon} />
                <Typography className={classes.yellowCardText}>
                  {t('NOTIFY_AFTER_POWER_RESTORED')}
                </Typography>
              </div>
              <div className={classes.contactBox}>
                <FormControl component="fieldset" className={''}>
                  <FormGroup className={classes.contactBoxDetails}>
                    {contactOptions.length > 0 &&
                    contactOptions.some(option => option?.value) ? (
                      contactOptions.map(
                        (option, index) =>
                          option?.value && (
                            <Box key={option?.id || `contact-${index}`}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!option?.isSelected}
                                    onChange={handleCheckBoxChange}
                                    name={option?.id}
                                    color="primary"
                                  />
                                }
                                label={option?.value}
                              />
                              <Typography className={classes.nameStyles}>
                                {customerName}
                              </Typography>
                            </Box>
                          ),
                      )
                    ) : (
                      <Box>
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={isCallbackPhoneSelected}
                            onChange={handleCallbackPhoneCheckboxChange}
                            name={'callbackPhoneCheckbox'}
                            color="primary"
                            style={{ marginTop: '8px', marginRight: '8px' }}
                          />
                          <Box flex={1}>
                            <PhoneTextField
                              size={'small'}
                              placeholder={t('PHONE_NUMBER')}
                              label={''}
                              name={'callbackPhone'}
                              autoComplete={'telephone'}
                              value={callbackPhone}
                              disabled={!isCallbackPhoneSelected}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setCallbackPhone(event.target.value);
                              }}
                              fullWidth
                            />
                          </Box>
                        </Box>
                        <Typography className={classes.customPhoneNameStyles}>
                          {customerName}
                        </Typography>
                      </Box>
                    )}
                  </FormGroup>
                </FormControl>
              </div>
            </div>
          )}
        </Box>
        <CustomButtons
          buttonOne={t('CANCEL')}
          buttonOneOnClick={() => {
            setReportOutageOpen(false);
          }}
          buttonTwo={
            <>
              <IconBolt className={classes.boltIcon} />
              {t('REPORT_OUTAGE')}
            </>
          }
          buttonTwoOnClick={() => sendReportOutage()}
        />
      </>
    );
  };

  return (
    <>
      {isReportOutageSuccess ? (
        <>
          <OutageAcknowledgementCard
            icon={<GreenCheckIcon />}
            cardHeader={t('WE_ARE_ON_IT')}
            content={richT('OUR_TEAM_WILL_REVIEW')}
            buttonOne={t('CLOSE')}
            buttonOneOnClick={() => {
              void buttonOneOnSubmit();
            }}
          />
        </>
      ) : (
        <>
          {isMobile ? (
            isAuthenticated &&
            outageMarkerView !== OutageMarkerView.NoOutage ? (
              <OutageTabs
                myAccountPanel={renderContent()}
                anotherLocationPanel={<ReportOutageOtherLocationDetails />}
                tabNumber={tabNumber}
                setTabNumber={setTabNumber}
              />
            ) : (
              renderContent()
            )
          ) : (
            <>
              {toggleSwitch ? (
                <>
                  <div className={classes.switchDiv}>{renderSwitchComp()}</div>
                  <ReportOutageOtherLocationDetails />
                </>
              ) : (
                <>
                  {isAuthenticated &&
                    outageMarkerView !== OutageMarkerView.NoOutage && (
                      <div className={classes.switchDiv}>
                        {renderSwitchComp()}
                      </div>
                    )}
                  {renderContent()}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
